import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Inject, Toolbar } from '@syncfusion/ej2-react-grids';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Tooltip, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function RuleSetList() {
    const [gridData, setGridData] = useState({ result: [], count: 0 });
    const [pageSettings, setPageSettings] = useState({
        pageSize: 20,
        currentPage: 1,
        totalRecordsCount: 0
    });
    const [loading, setLoading] = useState(false);

    // Form dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({
        name: '',
        description: '',
    });
    const [formError, setFormError] = useState('');
    const navigate = useNavigate();

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMd = useMediaQuery(theme.breakpoints.only('md'));
    let gridHeight;
    if (isXs || isSm || isMd) {
        gridHeight = '100%';
    }

    const fetchData = async () => {
        setLoading(true);
        const res = await fetch(`/api/rules?page=${pageSettings.currentPage}&pageSize=${pageSettings.pageSize}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        setGridData({
            result: formatData(data.items),
            count: data.total
        });
        setLoading(false);
    }

    // Fetch RuleSets
    useEffect(() => {
        fetchData();
    }, [pageSettings]);

    // Open dialog for add
    const handleOpenDialog = () => {
        setForm({ name: '', description: '' });
        setFormError('');
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Save (create)
    const handleSave = async () => {
        if (!form.name.trim()) {
            setFormError('Name is required');
            return;
        }
        const payload = {
            name: form.name,
            description: form.description,
            rules: [],
        };
        await fetch('/api/rules', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        setDialogOpen(false);
        // Refresh
        await fetchData();
    };

    const handleActionBegin = (args) => {
        if (args.requestType === 'paging') {
            setPageSettings({
                ...pageSettings,
                currentPage: args.currentPage,
            });
        }
    };

    const formatData = (data) => {
        return data.map(item => ({
            ...item,
            activeVersion: `v${item.activeVersion}` || 'N/A'
        }));
    };

    // Button column template
    const enterRuleSetButton = (props) => (
        <Tooltip title="Open RuleSet">
            <IconButton onClick={() => navigate(`/workflow/rules/${props.id}`)} size="small">
                <VisibilityIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                height: '100%',
                width: '100%',
                minHeight: 0
            }}
        >
            <Box
                sx={{
                    width: '90%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box
                    sx={{
                        alignSelf: 'flex-end',
                        alignItems: 'end',
                        mb: 2
                    }}
                >
                    <Tooltip title="Add new RuleSet">
                        <IconButton onClick={() => handleOpenDialog()}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box
                sx={{
                    width: '90%',
                    minHeight: 350,
                    height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 180px)' },
                    maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 180px)' },
                }}
            >
                <GridComponent
                    dataSource={gridData}
                    allowPaging={true}
                    pageSettings={pageSettings}
                    loadingIndicator={{ indicatorType: 'Shimmer' }}
                    actionBegin={handleActionBegin}
                >
                    <ColumnsDirective>
                        <ColumnDirective field="name" headerText="Name" width="180" />
                        <ColumnDirective field="description" headerText="Description" width="250" />
                        <ColumnDirective field="activeVersion" headerText="Active Version" width="100" />
                        <ColumnDirective field="createdBy" headerText="Created By" width="150" />
                        <ColumnDirective field="createdAt" headerText="Created At" width="180" format="yMd" type="date" />
                        <ColumnDirective
                            headerText="Actions"
                            width="100"
                            template={enterRuleSetButton}
                        />
                    </ColumnsDirective>
                    <Inject services={[Page, Toolbar]} />
                </GridComponent>

            </Box>
            {/* Add RuleSet Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>Add RuleSet</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        fullWidth
                        margin="normal"
                        required
                        error={!!formError}
                        helperText={formError}
                    />
                    <TextField
                        label="Description"
                        value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
